import {
    Avatar, Button, Clickable, Col, Dropdown, Icon,
    Input, Row,
    Text, TextEditor
} from 'components';
import { Api, theme, uploadMedia } from 'lib';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SearchUsers } from 'screens/Admin/components';
import { Media, Stock } from './';

const types = [
  { text: 'clothing', value: 'Clothing' },
  { text: 'ticket', value: 'Ticket' }
];
const emptyStock = { description: '', qty: '', priceInUSD: '' };
const nameLimit = 64;

const Product = ({ closeModal, product, setDataId }) => {
  const [owner, setOwner] = useState(null),
    [media, setMedia] = useState([]),
    [name, setName] = useState(''),
    [type, setType] = useState(types[0]),
    [price, setPrice] = useState(0),
    [discount, setDiscount] = useState(0),
    [stock, setStock] = useState([emptyStock]),
    [description, setDescription] = useState(''),
    [errors, setErrors] = useState([]),
    isEditing = !!product,
    dispatch = useDispatch();

  useEffect(() => {
    setOwner(product?.owner || null);
    setName(product?.name || '');
    setDescription(product?.description || '');
    setPrice(product?.priceInUSD ? product.priceInUSD / 100 : 0);
    setDiscount(product?.discount || 0);
    setStock(product?.stock || [emptyStock]);
    setMedia(product?.media || []);
  }, [product]);

  function handleNumChange(num, isCurrency) {
    num = num ? Number(num) : 0;

    if (!isCurrency && num > 100) return setDiscount(100);

    isCurrency ? setPrice(num) : setDiscount(num);
  }

  function handleName({ target: { value = '' } }) {
    if (value.length > nameLimit) return;

    setName(value);
  }

  async function submit() {
    let newErrs = [];
    if (!owner) newErrs.push({ input: 'owner', err: 'Required' });
    if (!name) newErrs.push({ input: 'name', err: 'Required' });
    if (!description) newErrs.push({ input: 'description', err: 'Required' });
    if (!media.length) newErrs.push({ input: 'media', err: 'At least one image is required' });
    if (!type) newErrs.push({ input: 'type', err: 'Required' });
    if (!price) newErrs.push({ input: 'price', err: 'Required' });
    if (!stock.find((x) => x.description))
      newErrs.push({ input: 'stock', err: 'At least one type of stock is required' });

    setErrors(newErrs);
    if (newErrs.length) return;

    dispatch({ type: 'LOADING_BUTTON', payload: 'addProduct' });

    const uploadedMedia = await Promise.all(
      media.map((x, i) => {
        /*
          Guard for edit product,
          if there is no preview the media is already uploaded
        */
        if (!x.preview) return x;

        return uploadMedia(x, i);
      })
    );

    const data = {
      name,
      description,
      discount,
      stock,
      priceInUSD: price * 100,
      owner: owner._id,
      type: type.text,
      media: uploadedMedia,
      _id: product?._id
    };

    const res = await Api.post('/admin/updateProduct', data);
    if (!res) return dispatch({ type: 'LOADING_BUTTON', payload: null });

    dispatch({
      type: product?._id ? 'ADMIN_EDIT_PRODUCT' : 'ADMIN_ADD_PRODUCT',
      payload: res
    });
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: product?._id ? 'Product edited.' : 'Product published.' }
    });
    dispatch({ type: 'LOADING_BUTTON', payload: null });
    setDataId(res._id);
  }

  return (
    <Col start between ht="100%" pad="0 24px">
      <Col wid="100%" noFlex>
        <Row between marg="24px 0 0">
          <Text marg="0 16px 4px" b2 med>
            Owner
          </Text>
        </Row>

        {owner && (
          <Row noFlex start marg="0 0 0">
            <Row center between wid="100%" pad="0 0">
              <Row noFlex center>
                <Avatar small user={owner} />

                <Col marg="0 0 0 8px" noFlex>
                  <Text>{owner?.name}</Text>

                  <Text c1 col={theme.GREY_60}>
                    @{owner?.username}
                  </Text>
                </Col>
              </Row>

              <Clickable onClick={() => setOwner(null)}>
                <Icon type="close" dimensions={14} />
              </Clickable>
            </Row>
          </Row>
        )}

        <SearchUsers
          wid="calc(100% - 48px)"
          selected={owner}
          handleSelect={setOwner}
          noRounded
          error={errors.find((x) => x.input === 'owner')}
        />

        <Media
          media={media}
          setMedia={setMedia}
          error={!media.length && errors.find((x) => x.input === 'media')?.err}
        />

        <Input
          marg="24px 0 0"
          title="Name"
          placeholder="Name..."
          value={name}
          onChange={handleName}
          error={!name.length && errors.find((x) => x.input === 'name')?.err}
          limit={nameLimit}
        />

        {/*
          <Input
            marg='24px 0 0'
            title='Description'
            placeholder='Description...'
            multiline
            ht='100px'
            value={description}
            onChange={({ target }) => setDescription(target.value)}
            error={!description.length && errors.find(x => x.input === 'description')?.err}
          />
        */}

        <TextEditor
          onChange={(val) => setDescription(val)}
          value={description}
          title="Description"
          marg="24px 0 0"
        />

        <Dropdown
          admin
          options={types}
          selected={type.value}
          handleSelect={(e) => setType(e)}
          placeholder="Type"
          bg="#fff"
          rounded
          wid="100%"
          marg="24px 0 0"
          title="Type"
          error={errors.find((x) => x.input === 'type')?.err}
        />

        <Row marg="24px 0 0">
          <Input
            currency
            prefix="$"
            defaultValue={0}
            marg="0 24px 0 0"
            title="Price"
            placeholder="$"
            value={price}
            onValueChange={(e) => handleNumChange(e, true)}
            error={!price && price !== 0 && errors.find((x) => x.input === 'price')?.err}
          />

          <Input
            currency
            prefix="%"
            title="Discount"
            placeholder="%"
            value={discount}
            onValueChange={(e) => handleNumChange(e, false)}
          />
        </Row>

        <Stock
          stock={stock}
          setStock={setStock}
          price={price}
          error={errors.find((x) => x.input === 'stock')?.err}
        />

        <Row pad="54px 0 24px">
          <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

          <Button
            big
            onClick={submit}
            text={isEditing ? 'Edit' : 'Publish'}
            bg={theme.SECONDARY}
            action="addProduct"
          />
        </Row>
      </Col>
    </Col>
  );
};

export default Product;
